import Button from '@components/Button'
import Touchable from '@components/Touchable'
import { logger } from '@plug-core/logger'
import { Principal } from '@dfinity/principal'
import { getPrincipals, importKeypairWallet } from '@plug-core/handlers/walletActions/wallet'
import { Stack, Typography, useTheme } from '@mui/material'
import { ParsedIdentity, getIdentityFromPem } from '@utils/parsePem'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import { HiCheckBadge } from 'react-icons/hi2'
import { useNavigate } from 'react-router-dom'
import useFindTokens from '@plug-core/hooks/useFindTokens'

const WalletImportPrivateKey = () => {
  const [selectedFile, setSelectedFile] = useState<File>()
  const [parsedIdentity, setParsedIdentity] = useState<ParsedIdentity>()
  const [isError, setError] = useState(false)
  const [importLoading, setImportLoading] = useState(false)
  const [existingPrincipals, setExistingPrincipals] = useState<Principal[]>([])

  const navigate = useNavigate()
  const { palette } = useTheme()
  const { t } = useTranslation()
  const { findTokens } = useFindTokens()

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setSelectedFile(acceptedFiles[0])
    }
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/x-x509-ca-cert': ['.pem'] },
  })

  useEffect(() => {
    if (!selectedFile) return
    setParsedIdentity(undefined)

    let fileReader = new FileReader()
    fileReader.readAsText(selectedFile)

    fileReader.onloadend = () => {
      const content = fileReader.result
      try {
        setParsedIdentity(getIdentityFromPem(content))
        setError(false)
      } catch (e) {
        logger.error('Failed to parse identity from pem', { originalError: e })
        setError(true)
      }
    }
  }, [selectedFile])

  useEffect(() => {
    getPrincipals()
      .then(setExistingPrincipals)
      .catch((e) => logger.error('Failed to get principals', { originalError: e }))
  }, [])

  const fileLabelColor = useMemo(() => {
    if (isError) return palette.rainbow.red

    return parsedIdentity ? palette.rainbow.green : 'white'
  }, [isError, parsedIdentity, palette])

  const onImportClick = useCallback(() => {
    if (!parsedIdentity) return

    setImportLoading(true)
    const keyPair = JSON.stringify(parsedIdentity.identity.toJSON())

    importKeypairWallet(keyPair)
      .then(async (wallet) => {
        await findTokens(wallet.principal)
        navigate(-2)
      })
      .catch((e) => logger.error('Failed keypair wallet import', { originalError: e }))
  }, [parsedIdentity])

  const alreadyExists = useMemo(() => {
    if (!parsedIdentity) return false

    return existingPrincipals
      .map((p) => p.toString())
      .includes(parsedIdentity.identity.getPrincipal().toString())
  }, [existingPrincipals, parsedIdentity])

  const textLabel = isDragActive
    ? t('importPrivateKey.dropHere')
    : t('importPrivateKey.dragAndDrop')

  const renderPropertyRow = (title: string, value: string) => (
    <Stack spacing={1}>
      <Typography variant="body2">{title}</Typography>
      <Stack
        bgcolor={(theme) => theme.palette.background.paper}
        sx={{ borderRadius: 1 }}
        px={2}
        py={1}
      >
        <Typography>{value}</Typography>
      </Stack>
    </Stack>
  )

  const renderIdentityDetails = () => {
    if (!parsedIdentity) return

    return (
      <Stack justifyContent="space-between" flex={1}>
        <Stack spacing={2}>
          {renderPropertyRow('Principal', parsedIdentity.identity.getPrincipal().toString())}
          {renderPropertyRow('Identity Type', parsedIdentity.type)}
        </Stack>
        <Stack mb={3}>
          <Button
            variant="rainbow"
            onClick={onImportClick}
            loading={importLoading}
            disabled={alreadyExists}
            fullWidth
          >
            {alreadyExists ? t('importPrivateKey.alreadyExists') : t('importPrivateKey.import')}
          </Button>
        </Stack>
      </Stack>
    )
  }

  return (
    <Stack mx={2} flex={1}>
      <div {...getRootProps()}>
        <input {...getInputProps()} />
        <Touchable style={{ width: '100%' }}>
          <Stack
            bgcolor={(theme) => theme.palette.background.paper}
            my={2}
            px={2}
            py={2}
            borderRadius={1}
            sx={{ borderStyle: 'dashed' }}
            borderColor={fileLabelColor}
          >
            <Stack alignItems="center" spacing={1}>
              <HiCheckBadge size={30} color={fileLabelColor} />
              <Typography textAlign="center" color={fileLabelColor}>
                {selectedFile?.name || textLabel}
              </Typography>
            </Stack>
          </Stack>
        </Touchable>
      </div>
      {renderIdentityDetails()}
    </Stack>
  )
}

export default WalletImportPrivateKey
